import React, { useState } from 'react'
import { fetchAgents, addAgent, updateAgent, deleteAgent, uploadAgentFile } from '../lib/api'
import SpecialBadge from './SpecialBadge'

const STATUS_COLORS = {
  'активный': { bg: '#EAF3DE', color: '#27500A' },
  'приостановлен': { bg: '#FAEEDA', color: '#633806' },
  'заблокирован': { bg: '#FCEBEB', color: '#E24B4A' },
}

const EXPENSE_CATEGORIES = ['Аванс', 'Карманные деньги', 'Зарплата', 'Авиабилет', 'Отель/жильё', 'Еда', 'Командировка', 'Прочее']

function fmt(d) {
  if (!d) return '—'
  return d.split('-').reverse().join('.')
}

function AgentDetailModal({ agent: init, candidates, onSaved, onDeleted, onClose, isAdmin }) {
  const [agent, setAgent] = useState(init)
  const [tab, setTab] = useState('profile')
  const [editMode, setEditMode] = useState(!init.id)
  const [form, setForm] = useState({ ...init })
  const [saving, setSaving] = useState(false)

  const today = new Date().toISOString().split('T')[0]
  const [expForm, setExpForm] = useState({ category: 'Карманные деньги', amount: '', comment: '', date: today })
  const [expFile, setExpFile] = useState(null)
  const [savingExp, setSavingExp] = useState(false)
  const [openExpCats, setOpenExpCats] = useState(new Set())
  const [editExpIdx, setEditExpIdx] = useState(null)
  const [editExpForm, setEditExpForm] = useState({})

  const EMPTY_SEG = { from: '', to: '', departure_date: '', departure_time: '', arrival_date: '', arrival_time: '', from_tz: 'Africa/Harare', to_tz: 'Africa/Harare' }
  const ROUTE_TZS = [
    { label: 'Кабо-Верде (UTC-1)', value: 'Atlantic/Cape_Verde' },
    { label: 'Лондон (UTC+0/+1)', value: 'Europe/London' },
    { label: 'Берлин (UTC+1/+2)', value: 'Europe/Berlin' },
    { label: 'Лагос (UTC+1)', value: 'Africa/Lagos' },
    { label: 'Каир (UTC+2)', value: 'Africa/Cairo' },
    { label: 'Харare (UTC+2)', value: 'Africa/Harare' },
    { label: 'Йоханнесбург (UTC+2)', value: 'Africa/Johannesburg' },
    { label: 'Найроби (UTC+3)', value: 'Africa/Nairobi' },
    { label: 'Аддис-Абеба (UTC+3)', value: 'Africa/Addis_Ababa' },
    { label: 'Москва (UTC+3)', value: 'Europe/Moscow' },
    { label: 'Дубай (UTC+4)', value: 'Asia/Dubai' },
    { label: 'Манила (UTC+8)', value: 'Asia/Manila' },
    { label: 'Пекин (UTC+8)', value: 'Asia/Shanghai' },
  ]
  const [routeSegs, setRouteSegs] = useState([{ ...EMPTY_SEG }])
  const [routeTicket, setRouteTicket] = useState('')
  const [routePrice, setRoutePrice] = useState('')
  const [routeFile, setRouteFile] = useState(null)
  const [savingRoute, setSavingRoute] = useState(false)

  function setRouteSeg(idx, field, val) {
    setRouteSegs(segs => segs.map((s, i) => i === idx ? { ...s, [field]: val } : s))
  }
  function addRouteSeg() {
    setRouteSegs(segs => { const last = segs[segs.length - 1]; return [...segs, { ...EMPTY_SEG, from: last.to, from_tz: last.to_tz, to_tz: last.to_tz }] })
  }

  const [hotelForm, setHotelForm] = useState({ hotel: '', check_in: '', check_out: '', price: '', notes: '' })
  const [hotelFile, setHotelFile] = useState(null)
  const [savingHotel, setSavingHotel] = useState(false)

  function set(field, val) { setForm(f => ({ ...f, [field]: val })) }

  async function applyUpdate(updates) {
    await updateAgent(agent.id, updates)
    const next = { ...agent, ...updates }
    setAgent(next)
    onSaved(next)
  }

  async function handleSaveProfile() {
    setSaving(true)
    try {
      const saveData = { ...form, commission_type: 'фикс' }
      if (form.id) {
        await updateAgent(form.id, saveData)
        setAgent(f => ({ ...f, ...saveData }))
        onSaved({ ...agent, ...saveData })
        setEditMode(false)
      } else {
        const created = await addAgent(saveData)
        onSaved(created, true)
        setAgent(created)
        setForm(created)
        setEditMode(false)
      }
    } catch (e) { alert('Ошибка: ' + e.message) }
    finally { setSaving(false) }
  }

  async function handleAddExpense() {
    if (!expForm.amount) { alert('Введите сумму'); return }
    setSavingExp(true)
    try {
      let file = null
      if (expFile) file = await uploadAgentFile(agent.id, 'expense', expFile)
      const newExp = { ...expForm, amount: parseFloat(expForm.amount), file, id: Date.now() }
      await applyUpdate({ expenses: [...(agent.expenses || []), newExp] })
      setExpForm({ category: 'Карманные деньги', amount: '', comment: '', date: today })
      setExpFile(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
    finally { setSavingExp(false) }
  }

  async function handleDeleteExpense(idx) {
    if (!window.confirm('Удалить этот расход?')) return
    await applyUpdate({ expenses: (agent.expenses || []).filter((_, i) => i !== idx) })
  }

  async function handleAddRoute() {
    const first = routeSegs[0]
    if (!first.from || !first.to || !first.departure_date) { alert('Заполните откуда, куда и дату вылета'); return }
    setSavingRoute(true)
    try {
      let fileData = null
      if (routeFile) fileData = await uploadAgentFile(agent.id, 'route', routeFile)
      const tripId = Date.now()
      const newPoints = routeSegs.filter(s => s.from && s.to).map((s, i) => ({
        from: s.from, from_tz: s.from_tz, from_date: s.departure_date, from_time: s.departure_time,
        to: s.to, to_tz: s.to_tz, to_date: s.arrival_date || s.departure_date, to_time: s.arrival_time,
        ticket_number: routeTicket, price: routePrice ? parseFloat(routePrice) : 0,
        note: 'Авиабилет' + (routeSegs.length > 1 ? ' (сегмент ' + (i + 1) + ' из ' + routeSegs.length + ')' : ''),
        files: fileData ? [fileData] : [],
        _tripId: tripId,
      }))
      const updates = { route: [...(agent.route || []), ...newPoints] }
      if (routePrice && parseFloat(routePrice) > 0) {
        const expComment = first.from + ' → ' + routeSegs[routeSegs.length - 1].to + (routeTicket ? ' №' + routeTicket : '')
        updates.expenses = [...(agent.expenses || []), { category: 'Авиабилет', amount: parseFloat(routePrice), date: first.departure_date, comment: expComment, file: fileData, id: tripId, _tripId: tripId }]
      }
      await applyUpdate(updates)
      setRouteSegs([{ ...EMPTY_SEG }]); setRouteTicket(''); setRoutePrice(''); setRouteFile(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
    finally { setSavingRoute(false) }
  }

  async function handleDeleteRoute(idx) {
    if (!window.confirm('Удалить точку маршрута?')) return
    const pt = (agent.route || [])[idx]
    const newRoute = (agent.route || []).filter((_, i) => i !== idx)
    const updates = { route: newRoute }
    if (pt && pt._tripId) {
      // удаляем все точки этой поездки и связанный расход
      updates.route = (agent.route || []).filter((p, i) => i !== idx && p._tripId !== pt._tripId)
      updates.expenses = (agent.expenses || []).filter(e => e._tripId !== pt._tripId)
    } else if (pt && pt.from && pt.to) {
      // старые записи без _tripId — ищем расход по маршруту
      const commentPattern = pt.from + ' → ' + pt.to
      updates.expenses = (agent.expenses || []).filter(e => !(e.category === 'Авиабилет' && e.comment && e.comment.startsWith(commentPattern)))
    }
    await applyUpdate(updates)
  }

  function fmtD(d) { return d ? d.split('-').reverse().join('.') : '' }

  async function handleAddHotel() {
    if (!hotelForm.hotel) { alert('Введите название отеля'); return }
    setSavingHotel(true)
    try {
      let file = null
      if (hotelFile) file = await uploadAgentFile(agent.id, 'hotel', hotelFile)
      const bid = Date.now()
      const booking = { ...hotelForm, file, id: bid }
      const updates = { hotel_bookings: [...(agent.hotel_bookings || []), booking] }
      const price = parseFloat(hotelForm.price) || 0
      if (price > 0) {
        const expComment = hotelForm.hotel + (hotelForm.check_in ? ' c ' + fmtD(hotelForm.check_in) : '') + (hotelForm.check_out ? ' по ' + fmtD(hotelForm.check_out) : '')
        updates.expenses = [...(agent.expenses || []), { category: 'Отель/жильё', amount: price, date: hotelForm.check_in || new Date().toISOString().split('T')[0], comment: expComment, file, id: bid + 1 }]
      }
      await applyUpdate(updates)
      setHotelForm({ hotel: '', check_in: '', check_out: '', price: '', notes: '' })
      setHotelFile(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
    finally { setSavingHotel(false) }
  }

  async function handleDeleteHotel(idx) {
    if (!window.confirm('Удалить бронирование?')) return
    const b = (agent.hotel_bookings || [])[idx]
    const updates = { hotel_bookings: (agent.hotel_bookings || []).filter((_, i) => i !== idx) }
    if (b && parseFloat(b.price) > 0) {
      const expComment = b.hotel + (b.check_in ? ' c ' + fmtD(b.check_in) : '') + (b.check_out ? ' по ' + fmtD(b.check_out) : '')
      updates.expenses = (agent.expenses || []).filter(e => !(e.category === 'Отель/жильё' && e.comment === expComment))
    }
    await applyUpdate(updates)
  }

  const expenses = agent.expenses || []
  const expByCategory = expenses.reduce((acc, e, idx) => {
    const cat = e.category || 'Прочее'
    if (!acc[cat]) acc[cat] = { sum: 0, items: [] }
    acc[cat].sum += parseFloat(e.amount) || 0
    acc[cat].items.push({ ...e, _idx: idx })
    return acc
  }, {})
  const totalExp = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)

  // Баланс аванса
  const totalAdvances = expenses.filter(e => e.category === 'Аванс').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const advanceSpendings = candidates.flatMap(c =>
    (c.expenses || [])
      .filter(e => e.from_agent && e.agent_source_id === agent.id)
      .map(e => ({ candidate_name: c.name, amount: parseFloat(e.agent_amount) || 0, date: e.date, comment: e.comment, category: e.category }))
  )
  const totalSpent = advanceSpendings.reduce((s, e) => s + e.amount, 0)
  const advanceBalance = totalAdvances - totalSpent

  const route = agent.route || []
  const hotels = agent.hotel_bookings || []
  const agentCandidates = candidates.filter(c => c.agent_id === agent.id)

  const inp = { width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid #e0e0dc', boxSizing: 'border-box' }
  const lbl = { fontSize: 11, color: '#888', marginBottom: 4, display: 'block' }

  const tabs = agent.id ? [
    { key: 'profile', label: '📋 Профиль' },
    { key: 'expenses', label: `💰 Расходы${totalExp > 0 ? ' · ' + totalExp.toLocaleString('ru-RU') + ' ₽' : ''}` },
    { key: 'route', label: `✈ Маршрут${route.length ? ' (' + route.length + ')' : ''}` },
    { key: 'hotels', label: `🏨 Брони${hotels.length ? ' (' + hotels.length + ')' : ''}` },
  ] : []

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 600, boxShadow: '0 8px 40px rgba(0,0,0,0.18)', marginBottom: 24 }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0ec', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{agent.name || 'Новый агент'}</div>
            {agent.company && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{agent.company}</div>}
          </div>
          <button onClick={onClose} style={{ fontSize: 22, background: 'none', border: 'none', cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
        </div>

        {/* Tabs */}
        {tabs.length > 0 && (
          <div style={{ display: 'flex', borderBottom: '1px solid #f0f0ec', overflowX: 'auto' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '10px 14px', fontSize: 12, border: 'none', background: 'none', cursor: 'pointer', fontWeight: tab === t.key ? 600 : 400, color: tab === t.key ? '#1a1a1a' : '#888', borderBottom: tab === t.key ? '2px solid #1a1a1a' : '2px solid transparent', whiteSpace: 'nowrap' }}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div style={{ padding: 20, maxHeight: '72vh', overflowY: 'auto' }}>

          {/* ── ПРОФИЛЬ ── */}
          {(tab === 'profile' || !agent.id) && (
            <div>
              {agent.id && !editMode ? (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    {[
                      ['Страна / город', [agent.city, agent.country].filter(Boolean).join(', ') || '—'],
                      ['Телефон', agent.phone || '—'],
                      ['WhatsApp', agent.whatsapp || '—'],
                      ['Специализация', agent.specialization || '—'],
                      ['Паспорт', agent.passport_number || '—'],
                      ['Комиссия', agent.commission_value > 0 ? agent.commission_value + (agent.commission_type === 'процент' ? '%' : ' ₽') : '—'],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <div style={{ fontSize: 11, color: '#888' }}>{label}</div>
                        <div style={{ fontSize: 13, marginTop: 2 }}>{val}</div>
                      </div>
                    ))}
                    <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontSize: 11, color: '#888' }}>Статус</div>
                      <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 6, background: STATUS_COLORS[agent.status]?.bg || '#f5f5f3', color: STATUS_COLORS[agent.status]?.color || '#888' }}>{agent.status}</span>
                    </div>
                  </div>
                  {agent.notes && <div style={{ fontSize: 12, color: '#666', background: '#fafaf8', padding: '8px 12px', borderRadius: 8, marginBottom: 10 }}>{agent.notes}</div>}
                  {agentCandidates.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>Кандидаты ({agentCandidates.length})</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {agentCandidates.map(c => (
                          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fafaf8', borderRadius: 7, border: '1px solid #f0f0ec', fontSize: 12 }}>
                            <span style={{ flex: 1 }}>{c.name}</span>
                            {c.passport && <span style={{ color: '#aaa', fontSize: 11 }}>{c.passport}</span>}
                            <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 4, background: '#f0f0ec', color: '#666' }}>{c.status || '—'}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    {isAdmin && <button onClick={() => setEditMode(true)} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #e0e0dc', background: 'white', cursor: 'pointer' }}>✏ Редактировать</button>}
                    {isAdmin && <button onClick={() => onDeleted(agent.id, agent.name)} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, border: '1px solid #f0c0c0', background: '#fff5f5', color: '#E24B4A', cursor: 'pointer' }}>🗑 Удалить агента</button>}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <div style={{ gridColumn: '1/-1' }}>
                      <label style={lbl}>Имя / Название *</label>
                      <input value={form.name || ''} onChange={e => set('name', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Компания</label>
                      <input value={form.company || ''} onChange={e => set('company', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Статус</label>
                      <select value={form.status || 'активный'} onChange={e => set('status', e.target.value)} style={inp}>
                        <option value="активный">Активный</option>
                        <option value="приостановлен">Приостановлен</option>
                        <option value="заблокирован">Заблокирован</option>
                      </select>
                    </div>
                    <div>
                      <label style={lbl}>Страна</label>
                      <input value={form.country || ''} onChange={e => set('country', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Город</label>
                      <input value={form.city || ''} onChange={e => set('city', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Телефон</label>
                      <input value={form.phone || ''} onChange={e => set('phone', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>WhatsApp</label>
                      <input value={form.whatsapp || ''} onChange={e => set('whatsapp', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Специализация</label>
                      <input value={form.specialization || ''} onChange={e => set('specialization', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Паспорт</label>
                      <input value={form.passport_number || ''} onChange={e => set('passport_number', e.target.value)} style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Комиссия</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input type="number" value={form.commission_value || ''} onChange={e => set('commission_value', e.target.value)} placeholder="0" style={{ ...inp, width: 90 }} />
                        <span style={{ ...inp, width: 'auto', flex: 1, display: 'flex', alignItems: 'center', background: '#f5f5f3', color: '#888' }}>₽ фикс</span>
                      </div>
                    </div>
                    <div style={{ gridColumn: '1/-1' }}>
                      <label style={lbl}>Заметки</label>
                      <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleSaveProfile} disabled={saving} style={{ flex: 1, padding: '9px', borderRadius: 8, background: '#1a1a1a', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                    {editMode && <button onClick={() => setEditMode(false)} style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #e0e0dc', background: 'white', cursor: 'pointer', fontSize: 13 }}>Отмена</button>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── РАСХОДЫ ── */}
          {tab === 'expenses' && (
            <div>
              {totalAdvances > 0 && (
                <div style={{ background: advanceBalance >= 0 ? '#f0faf0' : '#fff5f5', border: `1px solid ${advanceBalance >= 0 ? '#c8e6c9' : '#f5c6c6'}`, borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>💳 Баланс аванса</div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: advanceSpendings.length > 0 ? 10 : 0 }}>
                    <div><span style={{ fontSize: 11, color: '#888' }}>Выдано</span><div style={{ fontWeight: 700, color: '#1a6b3a' }}>{totalAdvances.toLocaleString('ru-RU')} ₽</div></div>
                    <div><span style={{ fontSize: 11, color: '#888' }}>Потрачено</span><div style={{ fontWeight: 700, color: '#c07840' }}>{totalSpent.toLocaleString('ru-RU')} ₽</div></div>
                    <div><span style={{ fontSize: 11, color: '#888' }}>Остаток</span><div style={{ fontWeight: 700, color: advanceBalance >= 0 ? '#1a6b3a' : '#E24B4A', fontSize: 15 }}>{advanceBalance.toLocaleString('ru-RU')} ₽</div></div>
                  </div>
                  {advanceSpendings.length > 0 && (
                    <div>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>История списаний:</div>
                      {advanceSpendings.sort((a, b) => (b.date || '') > (a.date || '') ? 1 : -1).map((s, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, padding: '3px 0', borderTop: i > 0 ? '0.5px solid #e8e8e5' : 'none' }}>
                          <span style={{ color: '#bbb', whiteSpace: 'nowrap' }}>{s.date ? s.date.split('-').reverse().join('.') : '—'}</span>
                          <span style={{ flex: 1, color: '#555' }}>{s.candidate_name}{s.comment ? ` · ${s.comment}` : ''}</span>
                          <span style={{ fontWeight: 600, color: '#c07840', whiteSpace: 'nowrap' }}>−{s.amount.toLocaleString('ru-RU')} ₽</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {Object.keys(expByCategory).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 8 }}>Итого: {totalExp.toLocaleString('ru-RU')} ₽</div>
                  {Object.entries(expByCategory).sort((a, b) => b[1].sum - a[1].sum).map(([cat, { sum, items }]) => {
                    const isOpen = openExpCats.has(cat)
                    return (
                      <div key={cat} style={{ borderBottom: '0.5px solid #f5f5f3' }}>
                        <div onClick={() => setOpenExpCats(prev => { const n = new Set(prev); isOpen ? n.delete(cat) : n.add(cat); return n })} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', cursor: 'pointer', fontSize: 13, userSelect: 'none' }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 10, color: '#bbb', width: 10 }}>{isOpen ? '▼' : '▶'}</span>
                            <span style={{ color: '#444' }}>{cat}</span>
                            <span style={{ fontSize: 11, color: '#bbb' }}>({items.length})</span>
                          </div>
                          <span style={{ fontWeight: 600, color: '#378ADD' }}>{sum.toLocaleString('ru-RU')} ₽</span>
                        </div>
                        {isOpen && (
                          <div style={{ paddingBottom: 8 }}>
                            {items.sort((a, b) => (b.date || '') > (a.date || '') ? 1 : -1).map(e => (
                              editExpIdx === e._idx ? (
                                <div key={e._idx} style={{ background: '#f0f4fa', borderRadius: 8, padding: '8px 10px', marginBottom: 4 }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Категория</div>
                                      <select value={editExpForm.category} onChange={e2 => setEditExpForm(f => ({ ...f, category: e2.target.value }))} style={{ ...inp, fontSize: 11 }}>
                                        {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                      </select>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Сумма (₽)</div>
                                      <input inputMode="numeric" value={editExpForm.amount} onChange={e2 => setEditExpForm(f => ({ ...f, amount: e2.target.value.replace(/[^0-9.]/g, '') }))} style={{ ...inp, fontSize: 11 }} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Дата</div>
                                      <input type="date" value={editExpForm.date} onChange={e2 => setEditExpForm(f => ({ ...f, date: e2.target.value }))} style={{ ...inp, fontSize: 11 }} />
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Комментарий</div>
                                      <input value={editExpForm.comment || ''} onChange={e2 => setEditExpForm(f => ({ ...f, comment: e2.target.value }))} style={{ ...inp, fontSize: 11 }} />
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <button onClick={async () => {
                                      const updated = [...expenses]
                                      updated[e._idx] = { ...updated[e._idx], category: editExpForm.category, amount: parseFloat(editExpForm.amount) || 0, date: editExpForm.date, comment: editExpForm.comment }
                                      await applyUpdate({ expenses: updated })
                                      setEditExpIdx(null)
                                    }} style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', background: '#378ADD', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Сохранить</button>
                                    <button onClick={() => setEditExpIdx(null)} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #e0e0dc', background: 'white', cursor: 'pointer', color: '#888' }}>Отмена</button>
                                  </div>
                                </div>
                              ) : (
                              <div key={e._idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, background: '#fafaf8', marginBottom: 3, fontSize: 11 }}>
                                <span style={{ color: '#bbb', whiteSpace: 'nowrap', minWidth: 65 }}>{fmt(e.date)}</span>
                                {e.comment && <span style={{ color: '#aaa', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.comment}</span>}
                                {!e.comment && <span style={{ flex: 1 }} />}
                                {e.file && <a href={e.file.url} target="_blank" rel="noreferrer" onClick={ev => ev.stopPropagation()} style={{ fontSize: 11, color: '#378ADD', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}>📎</a>}
                                <span style={{ fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap' }}>{parseFloat(e.amount).toLocaleString('ru-RU')} ₽</span>
                                <button onClick={() => { setEditExpIdx(e._idx); setEditExpForm({ category: e.category, amount: String(e.amount), date: e.date || '', comment: e.comment || '' }) }} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, border: '1px solid #c8dff8', background: '#EBF3FB', color: '#378ADD', cursor: 'pointer', flexShrink: 0 }}>✎</button>
                                <button onClick={() => handleDeleteExpense(e._idx)} style={{ fontSize: 11, padding: '2px 5px', borderRadius: 4, border: '1px solid #f0c0c0', background: '#fff5f5', color: '#E24B4A', cursor: 'pointer', flexShrink: 0 }}>✕</button>
                              </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
              <div style={{ background: '#fafaf8', border: '1px solid #f0f0ec', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>+ Добавить расход</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div>
                    <label style={lbl}>Категория</label>
                    <select value={expForm.category} onChange={e => setExpForm(f => ({ ...f, category: e.target.value }))} style={inp}>
                      {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Сумма (₽)</label>
                    <input inputMode="numeric" value={expForm.amount} onChange={e => setExpForm(f => ({ ...f, amount: e.target.value.replace(/[^0-9.]/g, '') }))} placeholder="0" style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Дата</label>
                    <input type="date" value={expForm.date} onChange={e => setExpForm(f => ({ ...f, date: e.target.value }))} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Файл</label>
                    <input type="file" onChange={e => setExpFile(e.target.files[0])} style={{ fontSize: 12, marginTop: 2 }} />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={lbl}>Комментарий</label>
                    <input value={expForm.comment} onChange={e => setExpForm(f => ({ ...f, comment: e.target.value }))} placeholder="..." style={inp} />
                  </div>
                </div>
                <button onClick={handleAddExpense} disabled={savingExp} style={{ width: '100%', padding: '8px', borderRadius: 8, background: '#1a1a1a', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                  {savingExp ? 'Сохранение...' : 'Добавить'}
                </button>
              </div>
            </div>
          )}

          {/* ── МАРШРУТ ── */}
          {tab === 'route' && (
            <div>
              {route.length === 0 && <div style={{ color: '#bbb', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Маршрут пустой</div>}
              {route.map((pt, i) => {
                const isTrip = pt.from && pt.to
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, padding: '10px 12px', background: '#fafaf8', borderRadius: 8, border: '1px solid #f0f0ec' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#1a1a1a', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      {isTrip ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>✈ {pt.from} → {pt.to}</div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {pt.from_date && <span>Вылет: {fmt(pt.from_date)}{pt.from_time ? ' ' + pt.from_time : ''}</span>}
                            {pt.to_date && <span>Прилёт: {fmt(pt.to_date)}{pt.to_time ? ' ' + pt.to_time : ''}</span>}
                            {pt.ticket_number && <span>№{pt.ticket_number}</span>}
                            {pt.price > 0 && <span style={{ color: '#378ADD', fontWeight: 600 }}>{parseFloat(pt.price).toLocaleString('ru-RU')} ₽</span>}
                          </div>
                          {pt.files && pt.files.length > 0 && <a href={pt.files[0].url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#185FA5', textDecoration: 'none', marginTop: 2, display: 'block' }}>📎 {pt.files[0].name}</a>}
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{pt.city}</div>
                          {pt.date && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{fmt(pt.date)}</div>}
                          {pt.notes && <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{pt.notes}</div>}
                        </>
                      )}
                    </div>
                    <button onClick={() => handleDeleteRoute(i)} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, border: '1px solid #f0c0c0', background: '#fff5f5', color: '#E24B4A', cursor: 'pointer' }}>✕</button>
                  </div>
                )
              })}
              <div style={{ background: '#fafaf8', border: '1px solid #f0f0ec', borderRadius: 10, padding: '12px 14px', marginTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>+ Добавить поездку</div>
                {routeSegs.map((seg, idx) => (
                  <div key={idx} style={{ background: 'white', border: '1px solid #f0f0ec', borderRadius: 8, padding: '10px', marginBottom: 8 }}>
                    {routeSegs.length > 1 && <div style={{ fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 6 }}>Сегмент {idx + 1}</div>}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <div><label style={lbl}>Откуда *</label><input value={seg.from} onChange={e => setRouteSeg(idx, 'from', e.target.value)} style={inp} /></div>
                      <div><label style={lbl}>Куда *</label><input value={seg.to} onChange={e => setRouteSeg(idx, 'to', e.target.value)} style={inp} /></div>
                      <div><label style={lbl}>Дата вылета *</label><input type="date" value={seg.departure_date} onChange={e => setRouteSeg(idx, 'departure_date', e.target.value)} style={inp} /></div>
                      <div><label style={lbl}>Время вылета</label><input type="time" value={seg.departure_time} onChange={e => setRouteSeg(idx, 'departure_time', e.target.value)} style={inp} /></div>
                      <div><label style={lbl}>Дата прилёта</label><input type="date" value={seg.arrival_date} onChange={e => setRouteSeg(idx, 'arrival_date', e.target.value)} style={inp} /></div>
                      <div><label style={lbl}>Время прилёта</label><input type="time" value={seg.arrival_time} onChange={e => setRouteSeg(idx, 'arrival_time', e.target.value)} style={inp} /></div>
                      <div><label style={lbl}>Часовой пояс откуда</label><select value={seg.from_tz} onChange={e => setRouteSeg(idx, 'from_tz', e.target.value)} style={inp}>{ROUTE_TZS.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}</select></div>
                      <div><label style={lbl}>Часовой пояс куда</label><select value={seg.to_tz} onChange={e => setRouteSeg(idx, 'to_tz', e.target.value)} style={inp}>{ROUTE_TZS.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}</select></div>
                    </div>
                  </div>
                ))}
                <button onClick={addRouteSeg} style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, border: '1px solid #e0e0dc', background: 'white', cursor: 'pointer', marginBottom: 8 }}>+ Стыковка</button>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div><label style={lbl}>Номер билета</label><input value={routeTicket} onChange={e => setRouteTicket(e.target.value)} style={inp} /></div>
                  <div><label style={lbl}>Стоимость (₽)</label><input inputMode="numeric" value={routePrice} onChange={e => setRoutePrice(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0" style={inp} /></div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'inline-block', fontSize: 12, padding: '6px 12px', border: '1px dashed #d0d0cc', borderRadius: 7, cursor: 'pointer', color: '#888' }}>
                    📎 {routeFile ? routeFile.name : 'Прикрепить файл билета'}
                    <input type="file" accept="image/*,.pdf" onChange={e => setRouteFile(e.target.files[0])} style={{ display: 'none' }} />
                  </label>
                </div>
                <button onClick={handleAddRoute} disabled={savingRoute} style={{ width: '100%', padding: '8px', borderRadius: 8, background: '#1a1a1a', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                  {savingRoute ? 'Сохранение...' : 'Добавить'}
                </button>
              </div>
            </div>
          )}

          {/* ── БРОНИРОВАНИЯ ── */}
          {tab === 'hotels' && (
            <div>
              {hotels.length === 0 && <div style={{ color: '#bbb', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Бронирований нет</div>}
              {hotels.map((h, i) => (
                <div key={i} style={{ padding: '12px', background: '#fafaf8', borderRadius: 8, border: '1px solid #f0f0ec', marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>🏨 {h.hotel}</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>{fmt(h.check_in)} → {fmt(h.check_out)}</div>
                      {h.price && <div style={{ fontSize: 12, color: '#378ADD', marginTop: 2, fontWeight: 600 }}>{parseFloat(h.price).toLocaleString('ru-RU')} ₽</div>}
                      {h.notes && <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{h.notes}</div>}
                      {h.file && <a href={h.file.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#378ADD', textDecoration: 'none', display: 'inline-block', marginTop: 4 }}>📎 {h.file.name || 'Файл'}</a>}
                    </div>
                    <button onClick={() => handleDeleteHotel(i)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, border: '1px solid #f0c0c0', background: '#fff5f5', color: '#E24B4A', cursor: 'pointer', flexShrink: 0 }}>✕</button>
                  </div>
                </div>
              ))}
              <div style={{ background: '#fafaf8', border: '1px solid #f0f0ec', borderRadius: 10, padding: '12px 14px', marginTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>+ Добавить бронирование</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={lbl}>Отель / жильё *</label>
                    <input value={hotelForm.hotel} onChange={e => setHotelForm(f => ({ ...f, hotel: e.target.value }))} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Заезд</label>
                    <input type="date" value={hotelForm.check_in} onChange={e => setHotelForm(f => ({ ...f, check_in: e.target.value }))} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Выезд</label>
                    <input type="date" value={hotelForm.check_out} onChange={e => setHotelForm(f => ({ ...f, check_out: e.target.value }))} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Стоимость (₽)</label>
                    <input inputMode="numeric" value={hotelForm.price} onChange={e => setHotelForm(f => ({ ...f, price: e.target.value.replace(/[^0-9.]/g, '') }))} style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Файл (подтверждение)</label>
                    <input type="file" onChange={e => setHotelFile(e.target.files[0])} style={{ fontSize: 12, marginTop: 2 }} />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={lbl}>Заметки</label>
                    <input value={hotelForm.notes} onChange={e => setHotelForm(f => ({ ...f, notes: e.target.value }))} style={inp} />
                  </div>
                </div>
                <button onClick={handleAddHotel} disabled={savingHotel} style={{ width: '100%', padding: '8px', borderRadius: 8, background: '#1a1a1a', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                  {savingHotel ? 'Сохранение...' : 'Добавить'}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default function AgentsPage({ candidates, onClose, canAddAgent = true, isAdmin = false, initialAgentId = null }) {
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [search, setSearch] = useState('')

  React.useEffect(() => {
    fetchAgents().then(data => {
      setAgents(data)
      setLoading(false)
      if (initialAgentId) {
        const agent = data.find(a => a.id === initialAgentId)
        if (agent) setModal(agent)
      }
    }).catch(() => setLoading(false))
  }, [])

  const filtered = agents.filter(a => {
    if (!search) return true
    const q = search.toLowerCase()
    return a.name?.toLowerCase().includes(q) || a.country?.toLowerCase().includes(q) || a.city?.toLowerCase().includes(q) || a.specialization?.toLowerCase().includes(q)
  })

  function handleSaved(agent, isNew) {
    if (isNew) {
      setAgents(prev => [...prev, agent].sort((a, b) => a.name.localeCompare(b.name)))
    } else {
      setAgents(prev => prev.map(a => a.id === agent.id ? agent : a))
    }
    setModal(agent)
  }

  async function handleDelete(id, name) {
    if (!window.confirm('Удалить агента "' + name + '"?')) return
    try {
      await deleteAgent(id)
      setAgents(prev => prev.filter(a => a.id !== id))
      setModal(null)
    } catch (e) { alert('Ошибка: ' + e.message) }
  }

  const stats = {
    total: agents.length,
    active: agents.filter(a => a.status === 'активный').length,
    paused: agents.filter(a => a.status === 'приостановлен').length,
    blocked: agents.filter(a => a.status === 'заблокирован').length,
    totalExp: agents.reduce((s, a) => s + (a.expenses || []).reduce((ss, e) => ss + (parseFloat(e.amount) || 0), 0), 0),
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f3' }}>
      <header style={{ background: 'white', borderBottom: '1px solid #e8e8e5', padding: '0 24px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: '#888', padding: '4px 8px' }}>←</button>
            <h1 style={{ fontSize: 17, fontWeight: 600 }}>🤝 Агенты</h1><SpecialBadge />
          </div>
          {canAddAgent && <button onClick={() => setModal({})} style={{ padding: '7px 16px', borderRadius: 8, background: '#1a1a1a', color: 'white', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>+ Агент</button>}
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Всего', val: stats.total, bg: 'white', c: '#1a1a1a' },
            { label: 'Активных', val: stats.active, bg: '#EAF3DE', c: '#27500A' },
            { label: 'Приостановлено', val: stats.paused, bg: '#FAEEDA', c: '#633806' },
            { label: 'Заблокировано', val: stats.blocked, bg: '#FCEBEB', c: '#E24B4A' },
            { label: 'Расходы всего', val: stats.totalExp.toLocaleString('ru-RU') + ' ₽', bg: 'white', c: '#378ADD' },
          ].map(({ label, val, bg, c }) => (
            <div key={label} style={{ background: bg, border: '1px solid #e8e8e5', borderRadius: 10, padding: '12px 16px', flex: 1, minWidth: 120, textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 600, color: c }}>{val}</div>
              <div style={{ fontSize: 11, color: c === '#1a1a1a' ? '#888' : c }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: 'white', border: '1px solid #e8e8e5', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Поиск по имени, стране, специализации..." style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e0e0dc', background: '#fafaf8', boxSizing: 'border-box' }} />
        </div>

        {loading && <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Загрузка...</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {filtered.map(agent => {
            const agentCandidates = candidates.filter(c => c.agent_id === agent.id)
            const st = STATUS_COLORS[agent.status] || STATUS_COLORS['активный']
            const totalExp = (agent.expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
            return (
              <div key={agent.id} style={{ background: 'white', border: '1px solid #e8e8e5', borderRadius: 12, padding: 16, cursor: 'pointer', transition: 'border-color .15s' }}
                onMouseOver={e => e.currentTarget.style.borderColor = '#378ADD'}
                onMouseOut={e => e.currentTarget.style.borderColor = '#e8e8e5'}
                onClick={() => setModal(agent)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{agent.name}</div>
                    {agent.company && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{agent.company}</div>}
                  </div>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: st.bg, color: st.color, fontWeight: 500, flexShrink: 0 }}>{agent.status}</span>
                </div>
                {agent.country && <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>📍 {agent.city ? agent.city + ', ' : ''}{agent.country}</div>}
                {agent.specialization && <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>🎯 {agent.specialization}</div>}
                <div style={{ display: 'flex', gap: 8, fontSize: 12, flexWrap: 'wrap' }}>
                  {agent.phone && <a href={'tel:' + agent.phone} onClick={e => e.stopPropagation()} style={{ color: '#185FA5', textDecoration: 'none' }}>📞 {agent.phone}</a>}
                  {agent.whatsapp && <a href={'https://wa.me/' + agent.whatsapp.replace(/\D/g, '')} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: '#25D366', textDecoration: 'none' }}>💬 WA</a>}
                </div>
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f5f5f3', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888' }}>
                  <span>👥 {agentCandidates.length} кандидатов</span>
                  {totalExp > 0 && <span style={{ color: '#378ADD', fontWeight: 600 }}>💰 {totalExp.toLocaleString('ru-RU')} ₽</span>}
                  {agent.commission_value > 0 && <span>%: {agent.commission_value}{agent.commission_type === 'процент' ? '%' : ' ₽'}</span>}
                </div>
              </div>
            )
          })}
        </div>

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#bbb' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🤝</div>
            <div>Агентов не найдено</div>
          </div>
        )}
      </main>

      {modal !== null && (
        <AgentDetailModal
          agent={modal}
          candidates={candidates}
          onSaved={handleSaved}
          onDeleted={handleDelete}
          onClose={() => setModal(null)}
          isAdmin={isAdmin}
        />
      )}
    </div>
  )
}
